import {
  ArrowUp,
  Atom,
  CircleHelp,
  CircleStop,
  FastForward,
  Globe2,
  LoaderCircle,
  Paperclip,
  SlidersHorizontal,
  Square,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import { fileToAttachment, formatBytes } from "../lib/files";
import { useAppStore } from "../lib/store";
import type { Attachment, ModelInfo, Provider, QuestionSession } from "../lib/types";

interface ComposerProps {
  isStreaming: boolean;
  initialValue?: string;
  models: Record<Provider, ModelInfo[]>;
  questionSession: QuestionSession | undefined;
  onSend: (value: string, attachments: Attachment[], instruction: string) => void;
  onStop: () => void;
  onProceedQuestions: () => void;
  onSkipQuestions: () => void;
  onCancelQuestions: () => void;
  modeSelector: React.ReactNode;
}

export function Composer({
  isStreaming,
  initialValue = "",
  models,
  questionSession,
  onSend,
  onStop,
  onProceedQuestions,
  onSkipQuestions,
  onCancelQuestions,
  modeSelector,
}: ComposerProps) {
  const [value, setValue] = useState(initialValue);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [instruction, setInstruction] = useState("");
  const [showInstruction, setShowInstruction] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [processingFile, setProcessingFile] = useState(false);
  const [fileError, setFileError] = useState<string>();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { settings, setSettings, setParams } = useAppStore();
  const conversation = useAppStore((state) =>
    state.conversations.find((item) => item.id === state.activeConversationId),
  );
  const updateQuestionMode = (
    patch: Partial<NonNullable<typeof conversation>["questionMode"]>,
  ) => {
    if (!conversation) return;
    useAppStore.getState().updateConversation(conversation.id, {
      questionMode: { ...conversation.questionMode, ...patch },
    });
  };

  const submit = () => {
    if ((!value.trim() && !attachments.length) || isStreaming) return;
    onSend(value, attachments, instruction);
    setValue("");
    setAttachments([]);
    setInstruction("");
    setShowInstruction(false);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setProcessingFile(true);
    setFileError(undefined);
    try {
      // allSettled, not all: one unreadable file used to discard every other
      // attachment in the same drop, silently.
      const results = await Promise.allSettled([...files].map(fileToAttachment));
      const converted = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );
      const failed = results.length - converted.length;
      if (converted.length) setAttachments((current) => [...current, ...converted]);
      if (failed) {
        setFileError(
          `${failed} file su ${results.length} non leggibili e ignorati.`,
        );
      }
    } finally {
      setProcessingFile(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="composer-wrap">
      {showAdvanced && (
        <div className="parameter-panel">
          <label>
            <span>Temperatura <b>{settings.params.temperature.toFixed(1)}</b></span>
            <input type="range" min="0" max="2" step="0.1" value={settings.params.temperature} onChange={(event) => setParams({ temperature: Number(event.target.value) })} />
          </label>
          <label>
            <span>Top P <b>{settings.params.topP.toFixed(2)}</b></span>
            <input type="range" min="0" max="1" step="0.05" value={settings.params.topP} onChange={(event) => setParams({ topP: Number(event.target.value) })} />
          </label>
          <label>
            <span>Max output</span>
            {/* Clamped: the worker rejects maxTokens 0, and an emptied number
                input reports exactly that, which would fail every request. */}
            <input
              type="number"
              min="64"
              max="65536"
              value={settings.params.maxTokens}
              onChange={(event) =>
                setParams({
                  maxTokens: clamp(
                    Number(event.target.value),
                    64,
                    65_536,
                    settings.params.maxTokens,
                  ),
                })
              }
            />
          </label>
          <label>
            <span>Top K</span>
            <input
              type="number"
              min="0"
              max="1000"
              value={settings.params.topK}
              onChange={(event) =>
                setParams({
                  topK: clamp(Number(event.target.value), 0, 1000, settings.params.topK),
                })
              }
            />
          </label>
        </div>
      )}
      <div className="composer">
        {conversation?.questionMode.enabled && (
          <div className="question-mode-panel">
            <div className="question-mode-heading">
              <span><CircleHelp size={14} /> Modalità domande</span>
              <strong>{questionSession ? questionStatusLabel(questionSession.status) : "attiva"}</strong>
            </div>
            {!questionSession && (
              <div className="question-mode-config">
                <label>
                  <input
                    type="checkbox"
                    checked={conversation.questionMode.useChatModel}
                    onChange={(event) =>
                      updateQuestionMode({ useChatModel: event.target.checked })
                    }
                  />
                  Usa il modello della chat
                </label>
                {!conversation.questionMode.useChatModel && (
                  <>
                    <select
                      value={conversation.questionMode.provider}
                      onChange={(event) =>
                        updateQuestionMode({
                          provider: event.target.value as Provider,
                          model: "",
                        })
                      }
                    >
                      <option value="openrouter">OpenRouter</option>
                      <option value="gemini">Google AI Studio</option>
                      <option value="universal">Universal Endpoint</option>
                    </select>
                    <select
                      className={conversation.questionMode.model ? "" : "invalid"}
                      value={conversation.questionMode.model}
                      onChange={(event) =>
                        updateQuestionMode({ model: event.target.value })
                      }
                    >
                      <option value="">Seleziona modello domande…</option>
                      {models[conversation.questionMode.provider].map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.name} · {model.id}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <label>
                  Round
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={conversation.questionMode.maxRounds}
                    onChange={(event) =>
                      updateQuestionMode({
                        maxRounds: clamp(
                          Number(event.target.value),
                          1,
                          10,
                          conversation.questionMode.maxRounds,
                        ),
                      })
                    }
                  />
                </label>
                <label>
                  Domande
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={conversation.questionMode.maxQuestions}
                    onChange={(event) =>
                      updateQuestionMode({
                        maxQuestions: clamp(
                          Number(event.target.value),
                          1,
                          30,
                          conversation.questionMode.maxQuestions,
                        ),
                      })
                    }
                  />
                </label>
              </div>
            )}
            {questionSession && (
              <div className="question-session-actions">
                <span>
                  Round {questionSession.round} · {questionSession.questions.length} domande
                </span>
                <button onClick={onSkipQuestions} disabled={isStreaming}>Salta</button>
                <button onClick={onProceedQuestions} disabled={isStreaming}>
                  <FastForward size={13} /> Procedi
                </button>
                <button onClick={onCancelQuestions}>
                  <CircleStop size={13} /> Annulla
                </button>
              </div>
            )}
          </div>
        )}
        {attachments.length > 0 && (
          <div className="attachment-strip">
            {attachments.map((attachment) => (
              <div className="attachment-card" key={attachment.id}>
                <Paperclip size={15} />
                <span><strong>{attachment.name}</strong><small>{formatBytes(attachment.size)}</small></span>
                <button onClick={() => setAttachments((items) => items.filter((item) => item.id !== attachment.id))}><X size={13} /></button>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            event.target.style.height = "auto";
            event.target.style.height = `${Math.min(event.target.scrollHeight, 220)}px`;
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={
            questionSession?.status === "awaiting_user"
              ? "Rispondi a uno o più chiarimenti…"
              : settings.agentTeam.enabled
                ? "Assegna un obiettivo al Team…"
                : settings.council.enabled
                  ? "Chiedi al Council…"
                  : "Scrivi un messaggio…"
          }
        />
        {showInstruction && (
          <div className="message-instruction">
            <span>Istruzione per questo messaggio</span>
            <input
              autoFocus
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder="Es. rispondi come tabella comparativa"
            />
            <button
              onClick={() => {
                setInstruction("");
                setShowInstruction(false);
              }}
              aria-label="Rimuovi istruzione"
            >
              <X size={12} />
            </button>
          </div>
        )}
        {fileError && <div className="composer-alert">{fileError}</div>}
        <div className="composer-toolbar">
          <div className="composer-tools">
            <input
              ref={fileRef}
              hidden
              type="file"
              multiple
              onChange={(event) => void onFiles(event.target.files)}
            />
            <button
              onClick={() => fileRef.current?.click()}
              title="Allega file"
              aria-label="Allega file"
              disabled={processingFile}
            >
              {processingFile ? (
                <LoaderCircle className="spin" size={16} />
              ) : (
                <Paperclip size={16} />
              )}
            </button>
            <button
              className={settings.webSearch ? "active" : ""}
              aria-pressed={settings.webSearch}
              onClick={() =>
                setSettings({ webSearch: !settings.webSearch, deepResearch: false })
              }
              title="Ricerca web"
              aria-label="Ricerca web"
            >
              <Globe2 size={16} />
            </button>
            <button
              className={settings.deepResearch ? "active" : ""}
              aria-pressed={settings.deepResearch}
              onClick={() =>
                setSettings({ deepResearch: !settings.deepResearch, webSearch: false })
              }
              title="Ricerca approfondita"
              aria-label="Ricerca approfondita"
            >
              <Atom size={16} />
            </button>
            <button
              className={conversation?.questionMode.enabled ? "active" : ""}
              aria-pressed={conversation?.questionMode.enabled ?? false}
              onClick={() =>
                updateQuestionMode({ enabled: !conversation?.questionMode.enabled })
              }
              title="Modalità domande"
              aria-label="Modalità domande"
            >
              <CircleHelp size={16} />
            </button>
            <button
              className={showInstruction ? "active" : ""}
              aria-pressed={showInstruction}
              onClick={() => setShowInstruction(!showInstruction)}
              title="Istruzione per questo messaggio"
              aria-label="Istruzione per questo messaggio"
            >
              <span className="tool-text">i</span>
            </button>
            <button
              className={showAdvanced ? "active" : ""}
              aria-pressed={showAdvanced}
              onClick={() => setShowAdvanced(!showAdvanced)}
              title="Parametri"
              aria-label="Parametri"
            >
              <SlidersHorizontal size={16} />
            </button>
            <span className="composer-divider" />
            {modeSelector}
          </div>
          <div className="composer-mode">
            {settings.deepResearch && (
              <span className="flag-chip">
                <Atom size={11} /> Deep research
              </span>
            )}
            {settings.webSearch && (
              <span className="flag-chip">
                <Globe2 size={11} /> Web
              </span>
            )}
            {settings.council.enabled && (
              <span className="flag-chip accent">
                Council ×{settings.council.models.length}
              </span>
            )}
            {settings.agentTeam.enabled && (
              <span className="flag-chip accent">
                Team ×{settings.agentTeam.agents.length}
              </span>
            )}
          </div>
          {isStreaming ? (
            <button className="send-button stop" onClick={onStop} aria-label="Interrompi">
              <Square size={14} fill="currentColor" />
            </button>
          ) : (
            <button
              className="send-button"
              onClick={submit}
              aria-label="Invia"
              disabled={!value.trim() && !attachments.length}
            >
              <ArrowUp size={17} />
            </button>
          )}
        </div>
      </div>
      <div className="composer-note">
        Vesper può commettere errori. Verifica le informazioni importanti.
      </div>
    </div>
  );
}

/** Number inputs report 0 when emptied and NaN while mid-edit. */
function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

function questionStatusLabel(status: QuestionSession["status"]): string {
  switch (status) {
    case "analyzing":
      return "analisi della richiesta";
    case "collecting":
      return "raccolta informazioni";
    case "awaiting_user":
      return "in attesa dell'utente";
    case "sufficient":
      return "informazioni sufficienti";
    case "limit_reached":
      return "limite raggiunto";
    case "cancelled":
      return "annullato";
    case "generating_final":
      return "generazione risposta finale";
  }
}
